import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaCheck,
  FaCheckDouble,
  FaExternalLinkAlt,
  FaShoppingBag,
  FaSyncAlt,
} from "react-icons/fa";
import "../../Styles/Seller/Notifications.css";
import sellerApi from "../../utils/sellerApi";

const FILTERS = [
  { label: "All", value: "all" },
  { label: "Unread", value: "unread" },
  { label: "Read", value: "read" },
];

const emptyPagination = {
  page: 1,
  limit: 20,
  total: 0,
  totalPages: 1,
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString();
};

const getEmptyMessage = (status) =>
  status === "unread"
    ? "You have no unread notifications."
    : "No notifications found.";

function Notifications() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [notifications, setNotifications] = useState([]);
  const [pagination, setPagination] = useState(emptyPagination);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [markingAll, setMarkingAll] = useState(false);

  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await sellerApi.get("seller/notifications", {
        params: { page, limit: 20, status },
      });
      const data = response.data?.data || {};
      setNotifications(data.notifications || []);
      setPagination(data.pagination || emptyPagination);
      setUnreadCount(Number(data.unreadCount || 0));
    } catch {
      setError("Unable to load notifications. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [page, status]);

  useEffect(() => {
    void loadNotifications();
  }, [loadNotifications]);

  useEffect(() => {
    const handleFocus = () => void loadNotifications();
    const handleVisibilityChange = () => {
      if (!document.hidden) void loadNotifications();
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [loadNotifications]);

  const changeStatus = (nextStatus) => {
    setStatus(nextStatus);
    setPage(1);
  };

  const markAsRead = async (notification) => {
    if (notification.isRead) return;

    try {
      setBusyId(notification.id);
      const response = await sellerApi.patch(
        `seller/notifications/${notification.id}/read`
      );
      setUnreadCount(Number(response.data?.unreadCount || 0));
      setNotifications((current) =>
        current.map((item) =>
          item.id === notification.id ? { ...item, isRead: true } : item
        )
      );
    } finally {
      setBusyId(null);
    }
  };

  const openNotificationAction = async (notification) => {
    await markAsRead(notification);
    navigate(notification.actionUrl || "/seller/order");
  };

  const markAllAsRead = async () => {
    try {
      setMarkingAll(true);
      await sellerApi.patch("seller/notifications/read-all");
      setUnreadCount(0);
      setNotifications((current) =>
        current.map((notification) => ({ ...notification, isRead: true }))
      );
    } finally {
      setMarkingAll(false);
    }
  };

  return (
    <main className="seller-notifications-page">
      <section className="seller-notifications-toolbar">
        <div>
          <h1>Notifications</h1>
          {unreadCount > 0 && (
            <span>{unreadCount > 99 ? "99+" : unreadCount} unread</span>
          )}
        </div>

        <div className="seller-notifications-actions">
          <div className="seller-notifications-filters">
            {FILTERS.map((filter) => (
              <button
                type="button"
                key={filter.value}
                className={status === filter.value ? "active" : ""}
                onClick={() => changeStatus(filter.value)}
              >
                {filter.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="seller-notifications-mark-all"
            onClick={markAllAsRead}
            disabled={!unreadCount || markingAll}
          >
            <FaCheckDouble />
            Mark all read
          </button>
        </div>
      </section>

      {error && (
        <section className="seller-notifications-error-panel">
          <span>{error}</span>
          <button type="button" onClick={loadNotifications}>
            <FaSyncAlt />
            Retry
          </button>
        </section>
      )}

      {loading ? (
        <section className="seller-notifications-list">
          {[0, 1, 2].map((item) => (
            <article className="seller-notification-card skeleton" key={item}>
              <span />
              <span />
              <span />
            </article>
          ))}
        </section>
      ) : notifications.length ? (
        <section className="seller-notifications-list">
          {notifications.map((notification) => {
            const metadata = notification.metadata || {};
            const items = Array.isArray(metadata.items) ? metadata.items : [];

            return (
              <article
                key={notification.id}
                className={`seller-notification-card ${
                  notification.isRead ? "" : "unread"
                }`}
              >
                <div className="seller-notification-card-main">
                  <div className="seller-notification-card-icon">
                    <FaShoppingBag />
                  </div>
                  <div className="seller-notification-card-content">
                    <div className="seller-notification-card-title-row">
                      {!notification.isRead && <span className="unread-dot" />}
                      <h2>{notification.title}</h2>
                      <small>{notification.isRead ? "Read" : "Unread"}</small>
                    </div>
                    <p>{notification.message}</p>

                    <div className="seller-notification-card-meta">
                      <div>
                        <span>Order</span>
                        <strong>
                          {metadata.orderNumber || notification.orderId || "-"}
                        </strong>
                      </div>
                      <div>
                        <span>Order Date</span>
                        <strong>{metadata.orderDate || "-"}</strong>
                      </div>
                      <div>
                        <span>Notification</span>
                        <strong>{formatDateTime(notification.createdAt)}</strong>
                      </div>
                    </div>

                    <div className="seller-notification-items">
                      {items.map((item) => (
                        <div
                          className="seller-notification-product"
                          key={item.orderItemId || item.productId || item.productName}
                        >
                          <strong>{item.productName || "Product"}</strong>
                          <span>Item Code: {item.itemCode || "-"}</span>
                          <span>Quantity: {item.quantity || 1}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="seller-notification-card-actions">
                  {!notification.isRead && (
                    <button
                      type="button"
                      className="secondary"
                      disabled={busyId === notification.id}
                      onClick={() => void markAsRead(notification)}
                    >
                      <FaCheck />
                      Mark read
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => void openNotificationAction(notification)}
                  >
                    <FaExternalLinkAlt />
                    View Order
                  </button>
                </div>
              </article>
            );
          })}
        </section>
      ) : (
        <section className="seller-notification-empty-panel">
          {getEmptyMessage(status)}
        </section>
      )}

      {pagination.totalPages > 1 && (
        <section className="seller-notifications-pagination">
          <span>
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <div>
            <button
              type="button"
              disabled={pagination.page <= 1}
              onClick={() => setPage((current) => Math.max(current - 1, 1))}
            >
              Previous
            </button>
            <button
              type="button"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() =>
                setPage((current) =>
                  Math.min(current + 1, pagination.totalPages)
                )
              }
            >
              Next
            </button>
          </div>
        </section>
      )}
    </main>
  );
}

export default Notifications;
